import { FieldErrors, UnpackNestedValue } from 'react-hook-form'
import capitalize from 'lodash/capitalize'
import flatten from 'lodash/flatten'
import get from 'lodash/get'
import { IShipmentStop, CreateLoadSchemaType } from 'apps/fleet-planner/schema'
import { getFieldStops } from 'apps/fleet-planner/utils/getFieldsByStopType'

type PathKeyName =
  | 'dispatcherId'
  | 'externalId'
  | 'drivers'
  | 'motorCarrier'
  | 'drivers'
  | 'addressLine1'
  | 'postalCode'
  | 'subdivisionCode'
  | 'locality'
  | 'startAt'
  | 'endAt'
  | 'timezone'
  | 'name'

export interface IPath {
  keyName: PathKeyName
  path: string
  required?: boolean
}

export interface IField {
  label: string
  keyName: string
  fields: IPath[]
}

export interface IStep {
  label: string
  keyName: string
  fields: IField[]
  classId: string
}

interface IValue {
  keyName: string
  label: string
  error: Record<string, any>
  value: Record<string, any>
}

/**
 * Returns an object with all selected values
 */
export function getValues<T extends Partial<UnpackNestedValue<CreateLoadSchemaType>>>(
  paths: IPath[],
  values: T
): Partial<Record<PathKeyName, any>> {
  const valuesObj: Partial<Record<string, any>> = {}
  for (let i = 0; i < paths.length; i += 1) {
    valuesObj[paths[i].keyName] = get(values, paths[i].path)
  }
  return valuesObj
}

/**
 * Gets error object
 */
export function getErrors<T extends FieldErrors<CreateLoadSchemaType>>(
  paths: IPath[],
  errors: T
): Partial<Record<PathKeyName, any>> {
  const errorsObj: any = {}
  for (let i = 0; i < paths.length; i += 1) {
    if (get(errors, paths[i].path)?.message) {
      errorsObj[paths[i].keyName] = get(errors, paths[i].path)?.message
    }
  }
  return errorsObj
}

interface ICreateValueGetterProps<T extends CreateLoadSchemaType = CreateLoadSchemaType> {
  errors: FieldErrors<T>
  values: Partial<UnpackNestedValue<T>>
}

/**
 * Creates a function to access values based on path
 */
export const createValueGetter = ({ errors, values }: ICreateValueGetterProps) => (
  fields: IPath[],
  label: string = '',
  keyName = ''
): IValue => {
  const value = getValues(fields, values)
  const error = getErrors(fields, errors)
  return { keyName, label, error, value }
}

/**
 * Creates list of steps in sidebar
 */
export const getStepsArray = (
  stops: IShipmentStop[],
  isFpCarrierPortalEnabled?: boolean
): IStep[] => {
  const steps: IStep[] = [
    {
      label: 'Basics',
      keyName: 'basics',
      classId: 'section-basics',
      fields: [
        {
          label: 'Load ID',
          keyName: 'loadId',
          fields: [{ keyName: 'externalId', path: 'externalId', required: true }],
        },
        {
          label: 'Driver',
          keyName: 'driver',
          fields: [{ keyName: 'drivers', path: 'drivers' }],
        },
        {
          label: 'Carrier',
          keyName: 'carrier',
          fields: [{ keyName: 'motorCarrier', path: 'motorCarrier' }],
        },
      ],
    },
  ]

  if (isFpCarrierPortalEnabled) {
    steps[0].fields.push({
      label: 'Dispatcher',
      keyName: 'dispatcher',
      fields: [{ keyName: 'dispatcherId', path: 'dispatcherId' }],
    })
  }

  const mapStopIndex = stops.map((stop: IShipmentStop, index) => {
    return { ...stop, ...{ stopIndex: index } }
  })

  interface Stop extends IShipmentStop {
    stopIndex: number
  }

  const { pickup = [], dropoff = [] } = getFieldStops(mapStopIndex)
  const createStopStep = (stop: Stop, stopLength: number): IStep => {
    const stopTypeLabel =
      stopLength > 1
        ? `${capitalize(stop.stopType)} ${stop.stopSequence}`
        : `${capitalize(stop.stopType)}`
    return {
      label: stopTypeLabel,
      keyName: `stops[${stop.stopIndex}]`,
      classId: `section-${stop.stopType}-${stop.stopSequence}`,
      fields: [
        {
          label: `${stopTypeLabel} location`,
          keyName: 'location',
          fields: [
            { keyName: 'addressLine1', path: `stops[${stop.stopIndex}].address.addressLine1` },
            { keyName: 'postalCode', path: `stops[${stop.stopIndex}].address.postalCode` },
            {
              keyName: 'subdivisionCode',
              path: `stops[${stop.stopIndex}].address.subdivisionCode`,
            },
            { keyName: 'locality', path: `stops[${stop.stopIndex}].address.locality` },
            { keyName: 'name', path: `stops[${stop.stopIndex}].facility.name`, required: true },
          ],
        },
        {
          label: `${stopTypeLabel} time`,
          keyName: 'time',
          fields: [
            { keyName: 'startAt', path: `stops[${stop.stopIndex}].startAt`, required: true },
            { keyName: 'endAt', path: `stops[${stop.stopIndex}].endAt` },
            { keyName: 'timezone', path: `stops[${stop.stopIndex}].timezone` },
          ],
        },
      ],
    }
  }

  /**
   * Stops section
   */
  pickup.forEach(stop => {
    steps.push(createStopStep(stop, pickup.length))
  })
  dropoff.forEach(stop => {
    steps.push(createStopStep(stop, dropoff.length))
  })

  const secondSteps = [
    {
      label: 'Load Details',
      keyName: 'load details',
      classId: 'section-load-details',
      fields: [],
    },
    {
      label: 'Attachments',
      keyName: 'attachments',
      classId: 'section-attachments',
      fields: [],
    },
  ]

  return [...steps, ...secondSteps]
}

/**
 * Returns array of `IPath` from all required fields
 */
export const getPathsFromRequiredFields = (fields: IField[] = []): IPath[] => {
  return flatten(fields.map(field => field.fields.map(path => path))).filter(p => p.required)
}
